/**
 * Сотрудники: чем сужается список и как это живёт в адресе.
 *
 * Отбирает и листает сервер (`GET /admin/staff` с `q`, `role_id`, `store_id`,
 * `page`, `page_size`) — экран только переводит своё состояние в параметры.
 */
import { isApiError } from '@dorixona/api'
import { failureText } from '../../ui/Load'
import type { StaffPageQuery } from './api'

/** Сколько строк на странице можно выбрать; по умолчанию — 10. */
export const PAGE_SIZES = [10, 25, 50] as const
export type PageSize = (typeof PAGE_SIZES)[number]

export interface StaffFilters {
  q: string
  /** 0 — любая роль. */
  role: number
  /** 0 — любая аптека (и «вся сеть» тоже). */
  store: number
  /** Страница с 1; листает сервер. */
  page: number
  size: PageSize
}

export const EMPTY_FILTERS: StaffFilters = { q: '', role: 0, store: 0, page: 1, size: 10 }

function positive(raw: string | null, fallback: number): number {
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : fallback
}

/**
 * Состояние экрана — в адресе: ссылкой на отобранный список можно
 * поделиться, а карточка роли ведёт сюда сразу с фильтром по роли. Мусор в
 * адресе не ломает экран — становится значением по умолчанию.
 */
export function filtersFromParams(params: URLSearchParams): StaffFilters {
  const size = positive(params.get('size'), EMPTY_FILTERS.size) as PageSize
  return {
    q: params.get('q') ?? '',
    role: positive(params.get('role'), 0),
    store: positive(params.get('store'), 0),
    page: positive(params.get('page'), 1),
    size: PAGE_SIZES.includes(size) ? size : EMPTY_FILTERS.size,
  }
}

/** Обратно в адрес — только то, что отличается от значения по умолчанию. */
export function filtersToParams(filters: StaffFilters): URLSearchParams {
  const params = new URLSearchParams()
  for (const key of Object.keys(EMPTY_FILTERS) as (keyof StaffFilters)[]) {
    if (filters[key] !== EMPTY_FILTERS[key]) params.set(key, String(filters[key]))
  }
  return params
}

/** Сужен ли список: страница и её размер — не фильтры. */
export const isFiltered = (filters: StaffFilters): boolean =>
  filters.q !== EMPTY_FILTERS.q ||
  filters.role !== EMPTY_FILTERS.role ||
  filters.store !== EMPTY_FILTERS.store

/** Состояние экрана → параметры `GET /admin/staff`. */
export const toStaffQuery = (filters: StaffFilters): StaffPageQuery => ({
  q: filters.q.trim(),
  roleId: filters.role,
  storeId: filters.store,
  page: filters.page,
  pageSize: filters.size,
})

/** Браузеры по строке User-Agent. Порядок важен: Edge и Opera пишут о себе и «Chrome». */
const BROWSERS: [RegExp, string][] = [
  [/YaBrowser\//, 'Яндекс Браузер'],
  [/Edg\//, 'Edge'],
  [/OPR\//, 'Opera'],
  [/Firefox\//, 'Firefox'],
  [/Chrome\//, 'Chrome'],
  [/Safari\//, 'Safari'],
]

/** Системы: iPhone и Android тоже называют себя «Mac OS X» и «Linux». */
const SYSTEMS: [RegExp, string][] = [
  [/iPhone|iPad/, 'iOS'],
  [/Android/, 'Android'],
  [/Windows/, 'Windows'],
  [/Macintosh/, 'macOS'],
  [/Linux/, 'Linux'],
]

const first = (rules: [RegExp, string][], agent: string): string =>
  rules.find(([pattern]) => pattern.test(agent))?.[1] ?? ''

/**
 * «Chrome · macOS» вместо сырой строки User-Agent: по ней сотрудник узнаёт
 * свой ноутбук или телефон. Незнакомую строку показываем как есть.
 */
export function describeAgent(agent: string): string {
  const known = [first(BROWSERS, agent), first(SYSTEMS, agent)].filter(Boolean).join(' · ')
  return known || agent || 'Неизвестное устройство'
}

/**
 * Отказ формы сотрудника. Занятый телефон или почта — `conflict`, неверные
 * поля — `validation_failed`: оба исправляются в самой форме, поэтому и текст про неё.
 */
export function staffFailure(error: unknown): string {
  if (isApiError(error) && error.code === 'conflict') {
    return 'Телефон или почта уже заняты другим сотрудником.'
  }
  if (isApiError(error) && error.code === 'validation_failed') {
    return 'Проверьте имя, телефон и почту.'
  }
  return failureText(error)
}
